import { CachePersistor, SessionStorageWrapper } from 'apollo3-cache-persist'
import { defaultDataIdFromObject, InMemoryCache, NormalizedCacheObject, StoreObject } from '@apollo/client'
import possibleTypes from '../possibleTypes.json'
import { SearchResultsListingsGridQuery_searchResults_SearchResults } from '../contexts/search-results-context/query/__generated__/search-results-listings.fed.query'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

export const FORCE_LOAD_RESULTS_FROM_CACHE = 'flrfc'

let cachePersistor: CachePersistor<NormalizedCacheObject> | undefined

const getDataIdForObject = (object: Readonly<StoreObject>) => {
    switch (object.__typename) {
        case 'SearchListing': {
            return `SearchListing:${object.advertId}-${object.type}`
        }
        case 'GPTListing': {
            return `GPTListing:${object.posId}`
        }
        case 'LeasingListing': {
            return `LeasingListing:${object.advertId}`
        }
        case 'SortByOptions': {
            return `SortByOptions:${object.value}`
        }
        case 'SortBy': {
            return `SortBy:${object.selected}`
        }
        case 'Facet': {
            return `Facet:${object.facet}`
        }
        case 'Filter': {
            return `Filter:${object.filter}`
        }
        case 'Badge': {
            return `Badge:${object.type}-${object.displayText}`
        }
        default: {
            return defaultDataIdFromObject(object)
        }
    }
}

const mergeFunction = (
    existing: SearchResultsListingsGridQuery_searchResults_SearchResults | null,
    incoming: SearchResultsListingsGridQuery_searchResults_SearchResults
) => {
    if (existing?.listings && incoming.listings) {
        if ((existing?.page?.number || 0) > (incoming.page?.number || 0)) {
            return {
                ...existing,
                listings: [...incoming.listings, ...existing.listings],
            }
        } else {
            return {
                ...existing,
                listings: [...existing.listings, ...incoming.listings],
                page: incoming.page,
            }
        }
    } else {
        return incoming
    }
}

const getKeyArgs: () => string[] = () => {
    return ['channel', 'facets', 'filters', 'imageCount', 'listingType', 'sortBy', 'useSearchEvoFacets']
}

const cache = new InMemoryCache({
    dataIdFromObject: getDataIdForObject,
    possibleTypes: possibleTypes,
    typePolicies: {
        Query: {
            fields: {
                searchResults: {
                    keyArgs: ['input', getKeyArgs()],
                },
            },
        },
        SearchResults: {
            merge: mergeFunction,
        },
    },
})
if (typeof window !== 'undefined') {
    cachePersistor = new CachePersistor<NormalizedCacheObject>({
        cache,
        storage: new SessionStorageWrapper(window.sessionStorage),
        trigger: false,
        maxSize: false,
        debug: process.env.NODE_ENV === 'development',
    })
}

const persistCache = async () => {
    await cachePersistor?.persist()
}

const purgeCache = () => {
    cache
        ?.reset()
        ?.then(() => cachePersistor?.purge())
        .then()
}

const useCacheRestorer = () => {
    const [cacheInitialised, setCacheInitialised] = useState(false)
    const { search, hash } = useLocation()
    useEffect(() => {
        if (search.includes(`${FORCE_LOAD_RESULTS_FROM_CACHE}=1`)) {
            cachePersistor?.restore().then(() => {
                setCacheInitialised(true)
                if (hash) {
                    setTimeout(() => {
                        document.getElementById(hash.substring(1))?.scrollIntoView({
                            behavior: 'instant',
                            block: 'start',
                        })
                    }, 100)
                }
            })
        } else {
            setCacheInitialised(true)
        }
    }, [search, hash])

    return { cacheInitialised }
}

export { cache, purgeCache, persistCache, useCacheRestorer }

export const exportedForTesting = { mergeFunction, getKeyArgs }
